import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { 
  ArrowLeft, Plus, Pencil, Trash2, Eye, EyeOff, Search, 
  Loader2, CheckCircle2, X, ChevronDown
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_LABELS = {
  self_awareness: 'Self Awareness',
  health: 'Health',
  relationship: 'Relationship',
  anxiety: 'Anxiety',
  depression: 'Depression',
  calmness: 'Calmness',
  honesty: 'Honesty',
  empathy: 'Empathy',
  social_skills: 'Social Skills',
  motivation: 'Motivation',
  fear: 'Fear'
};

const CATEGORY_COLORS = {
  self_awareness: 'bg-violet-100 text-violet-700',
  health: 'bg-rose-100 text-rose-700',
  relationship: 'bg-pink-100 text-pink-700',
  anxiety: 'bg-amber-100 text-amber-700',
  depression: 'bg-indigo-100 text-indigo-700',
  calmness: 'bg-plum-voltage/15 text-plum-voltage',
  honesty: 'bg-white/5 text-white',
  empathy: 'bg-teal-100 text-teal-700',
  social_skills: 'bg-blue-100 text-blue-700',
  motivation: 'bg-orange-100 text-orange-700',
  fear: 'bg-purple-100 text-purple-700'
};

const DEFAULT_OPTIONS = [
  { id: 'a', text: '', score: 1 },
  { id: 'b', text: '', score: 2 },
  { id: 'c', text: '', score: 3 },
  { id: 'd', text: '', score: 4 }
];

const QuestionForm = ({ question, onSave, onCancel }) => {
  const [form, setForm] = useState({
    question_type: question?.question_type || 'mcq',
    question_text: question?.question_text || '',
    scenario_context: question?.scenario_context || '',
    emotion_category: question?.emotion_category || 'self_awareness',
    options: question?.options || DEFAULT_OPTIONS.map(o => ({ ...o })),
    active: question?.active !== undefined ? question.active : true
  });
  const [saving, setSaving] = useState(false);

  const updateOption = (idx, field, value) => {
    setForm(f => {
      const opts = [...f.options];
      opts[idx] = { ...opts[idx], [field]: field === 'score' ? parseInt(value) || 0 : value };
      return { ...f, options: opts };
    });
  };

  const handleSubmit = async () => {
    if (!form.question_text.trim()) { toast.error('Question text required'); return; }
    if (form.options.some(o => !o.text.trim())) { toast.error('All options must have text'); return; }
    setSaving(true);
    await onSave(form);
    setSaving(false);
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="text-xs font-bold text-smoke uppercase tracking-wider mb-1 block">Category</label>
        <select value={form.emotion_category} onChange={e => setForm(f => ({ ...f, emotion_category: e.target.value }))}
          className="w-full h-10 rounded-xl border border-white/10 px-3 text-sm bg-black" data-testid="question-category-select">
          {Object.entries(CATEGORY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>

      <div>
        <label className="text-xs font-bold text-smoke uppercase tracking-wider mb-1 block">Type</label>
        <div className="flex gap-2">
          <button onClick={() => setForm(f => ({ ...f, question_type: 'mcq' }))}
            className={`flex-1 py-2 rounded-xl text-xs font-bold border transition-all ${form.question_type === 'mcq' ? 'bg-plum-voltage text-white border-slate-800' : 'bg-black text-smoke border-white/10'}`}>MCQ</button>
          <button onClick={() => setForm(f => ({ ...f, question_type: 'scenario' }))}
            className={`flex-1 py-2 rounded-xl text-xs font-bold border transition-all ${form.question_type === 'scenario' ? 'bg-plum-voltage text-white border-slate-800' : 'bg-black text-smoke border-white/10'}`}>Scenario</button>
        </div>
      </div>

      {form.question_type === 'scenario' && (
        <div>
          <label className="text-xs font-bold text-smoke uppercase tracking-wider mb-1 block">Scenario Context</label>
          <Textarea value={form.scenario_context} onChange={e => setForm(f => ({ ...f, scenario_context: e.target.value }))}
            placeholder="Describe the scenario..." className="rounded-xl text-sm min-h-[60px]" data-testid="scenario-context-input" />
        </div>
      )}

      <div>
        <label className="text-xs font-bold text-smoke uppercase tracking-wider mb-1 block">Question Text</label>
        <Textarea value={form.question_text} onChange={e => setForm(f => ({ ...f, question_text: e.target.value }))}
          placeholder="Enter the question..." className="rounded-xl text-sm min-h-[60px]" data-testid="question-text-input" />
      </div>

      <div>
        <label className="text-xs font-bold text-smoke uppercase tracking-wider mb-1 block">Options (Score: 1=Low EI, 4=High EI)</label>
        <div className="space-y-2">
          {form.options.map((opt, i) => (
            <div key={opt.id} className="flex items-center gap-2">
              <span className="w-7 h-7 rounded-lg bg-white/5 flex items-center justify-center text-xs font-bold text-smoke flex-shrink-0">{opt.id.toUpperCase()}</span>
              <Input value={opt.text} onChange={e => updateOption(i, 'text', e.target.value)}
                placeholder={`Option ${opt.id.toUpperCase()} text...`} className="rounded-xl text-sm flex-1" data-testid={`option-${opt.id}-input`} />
              <select value={opt.score} onChange={e => updateOption(i, 'score', e.target.value)}
                className="w-16 h-10 rounded-xl border border-white/10 text-sm text-center bg-black">
                {[1, 2, 3, 4].map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          ))}
        </div>
      </div>

      <div className="flex gap-3 pt-2">
        <Button onClick={onCancel} variant="outline" className="flex-1 rounded-full" data-testid="cancel-question-btn">Cancel</Button>
        <Button onClick={handleSubmit} disabled={saving} className="flex-1 rounded-full bg-plum-voltage hover:bg-black" data-testid="save-question-btn">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1" />}
          {question ? 'Update' : 'Add Question'}
        </Button>
      </div>
    </div>
  );
};

const AdminQuestions = () => {
  const [questions, setQuestions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [categoryCounts, setCategoryCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);

  const fetchQuestions = useCallback(async () => {
    try {
      const url = filter ? `${API}/admin/questions?category=${filter}` : `${API}/admin/questions`;
      const r = await fetch(url, { credentials: 'include' });
      if (r.status === 401 || r.status === 403) {
        toast.error('Admin access required. Please log in with an admin account.');
        setLoading(false);
        return;
      }
      const data = await r.json();
      setQuestions(data.questions);
      setCategories(data.categories);
      setCategoryCounts(data.category_counts);
    } catch { toast.error('Failed to load questions'); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { fetchQuestions(); }, [fetchQuestions]);

  const handleSeed = async () => {
    const r = await fetch(`${API}/admin/questions/seed`, { method: 'POST', credentials: 'include' });
    const data = await r.json();
    toast.success(data.message || `Seeded ${data.count} questions`);
    fetchQuestions();
  };

  const handleSave = async (form) => {
    const method = editingQuestion ? 'PUT' : 'POST';
    const url = editingQuestion ? `${API}/admin/questions/${editingQuestion.question_id}` : `${API}/admin/questions`;
    const r = await fetch(url, { method, headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(form) });
    if (r.ok) {
      toast.success(editingQuestion ? 'Question updated!' : 'Question added!');
      setDialogOpen(false);
      setEditingQuestion(null);
      fetchQuestions();
    } else {
      const err = await r.json();
      toast.error(err.detail || 'Failed to save');
    }
  };

  const handleDelete = async (qid) => {
    if (!window.confirm('Delete this question permanently?')) return;
    const r = await fetch(`${API}/admin/questions/${qid}`, { method: 'DELETE', credentials: 'include' });
    if (r.ok) { toast.success('Deleted'); fetchQuestions(); }
  };

  const handleToggle = async (qid) => {
    const r = await fetch(`${API}/admin/questions/${qid}/toggle`, { method: 'POST', credentials: 'include' });
    if (r.ok) fetchQuestions();
  };

  const filtered = questions.filter(q =>
    !searchTerm || q.question_text.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalActive = Object.values(categoryCounts).reduce((s, c) => s + (c.active || 0), 0);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-black/85 backdrop-blur-xl border-b border-white/10 sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" data-testid="back-home-btn">
              <Button variant="ghost" size="sm" className="rounded-full"><ArrowLeft className="h-4 w-4" /></Button>
            </Link>
            <div>
              <h1 className="text-base font-bold text-white">Question Bank</h1>
              <p className="text-[11px] text-smoke">{totalActive} active questions</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="rounded-full text-xs" onClick={handleSeed} data-testid="seed-btn">Seed Default</Button>
            <Button size="sm" className="rounded-full bg-plum-voltage hover:bg-black text-xs" data-testid="add-question-btn"
              onClick={() => { setEditingQuestion(null); setDialogOpen(true); }}>
              <Plus className="h-3.5 w-3.5 mr-1" /> Add
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
        {/* Category filters */}
        <div className="flex gap-2 overflow-x-auto pb-3 mb-4 no-scrollbar">
          <button onClick={() => setFilter('')}
            className={`px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap border transition-all ${!filter ? 'bg-plum-voltage text-white border-slate-800' : 'bg-black text-smoke border-white/10 hover:border-slate-300'}`}
            data-testid="filter-all">
            All ({questions.length})
          </button>
          {categories.map(cat => (
            <button key={cat} onClick={() => setFilter(cat === filter ? '' : cat)}
              className={`px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap border transition-all ${filter === cat ? 'bg-plum-voltage text-white border-slate-800' : 'bg-black text-smoke border-white/10 hover:border-slate-300'}`}
              data-testid={`filter-${cat}`}>
              {CATEGORY_LABELS[cat] || cat} ({categoryCounts[cat]?.active || 0})
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-smoke" />
          <Input value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            placeholder="Search questions..." className="pl-10 rounded-xl h-10" data-testid="search-questions" />
        </div>

        {/* Questions list */}
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-white" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-smoke text-sm mb-3">No questions found</p>
            <Button onClick={handleSeed} className="rounded-full bg-plum-voltage hover:bg-black">Seed Default Questions</Button>
          </div>
        ) : (
          <div className="space-y-3" data-testid="questions-list">
            {filtered.map(q => (
              <Card key={q.question_id} className={`rounded-2xl border-white/10 transition-all ${!q.active ? 'opacity-50' : ''}`} data-testid={`question-card-${q.question_id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                        <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold ${CATEGORY_COLORS[q.emotion_category] || 'bg-white/5 text-ash'}`}>
                          {CATEGORY_LABELS[q.emotion_category] || q.emotion_category}
                        </span>
                        <span className="text-[10px] text-smoke">{q.question_type.toUpperCase()}</span>
                        {!q.active && <span className="text-[10px] text-red-400 font-bold">INACTIVE</span>}
                      </div>
                      {q.scenario_context && <p className="text-[11px] text-smoke italic mb-1 line-clamp-1">{q.scenario_context}</p>}
                      <p className="text-sm text-ash font-medium line-clamp-2">{q.question_text}</p>
                      <div className="flex gap-1 mt-2 flex-wrap">
                        {q.options.map(o => (
                          <span key={o.id} className="text-[10px] bg-black text-smoke px-1.5 py-0.5 rounded border border-white/10">
                            {o.id.toUpperCase()}: {o.text.slice(0, 25)}{o.text.length > 25 ? '...' : ''} ({o.score})
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button onClick={() => handleToggle(q.question_id)} className="w-8 h-8 rounded-lg flex items-center justify-center text-smoke hover:bg-white/5 transition-colors"
                        title={q.active ? 'Deactivate' : 'Activate'} data-testid={`toggle-${q.question_id}`}>
                        {q.active ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
                      </button>
                      <button onClick={() => { setEditingQuestion(q); setDialogOpen(true); }} className="w-8 h-8 rounded-lg flex items-center justify-center text-smoke hover:bg-white/5 transition-colors"
                        data-testid={`edit-${q.question_id}`}><Pencil className="h-3.5 w-3.5" /></button>
                      <button onClick={() => handleDelete(q.question_id)} className="w-8 h-8 rounded-lg flex items-center justify-center text-red-300 hover:bg-red-500/10 hover:text-red-500 transition-colors"
                        data-testid={`delete-${q.question_id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg rounded-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingQuestion ? 'Edit Question' : 'Add New Question'}</DialogTitle>
          </DialogHeader>
          <QuestionForm question={editingQuestion} onSave={handleSave} onCancel={() => { setDialogOpen(false); setEditingQuestion(null); }} />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminQuestions;
